import {
  Box,
  Card,
  Chip,
  Stack,
  Typography,
  alpha,
  CircularProgress,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { RiskTickerRisk } from "../hooks/useLiveProgressRisks";

interface RiskTickerProps {
  risks: RiskTickerRisk[];
  isLoading?: boolean;
}

export const RiskTicker = ({
  risks,
  isLoading = false,
}: RiskTickerProps) => {
  const getImpactColor = (impact: string) => {
    switch (impact.toLowerCase()) {
      case "high":
        return "error";
      case "medium":
        return "warning";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  const getImpactIcon = (impact: string) => {
    switch (impact.toLowerCase()) {
      case "high":
        return <ErrorOutlineIcon sx={{ fontSize: 16 }} />;
      case "medium":
        return <WarningAmberIcon sx={{ fontSize: 16 }} />;
      case "low":
        return <InfoOutlinedIcon sx={{ fontSize: 16 }} />;
      default:
        return <InfoOutlinedIcon sx={{ fontSize: 16 }} />;
    }
  };

  if (isLoading) {
    return (
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          flexShrink: 0,
          height: 80,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(8px)",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.4)
              : alpha(theme.palette.background.paper, 0.8),
          boxShadow: (theme) => theme.shadows[15],
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.05)}`,
        }}
      >
        <CircularProgress size={24} />
      </Card>
    );
  }

  if (risks.length === 0) {
    return null;
  }

  const duplicatedRisks = [...risks, ...risks, ...risks];

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        flexShrink: 0,
        height: 80,
        overflow: "hidden",
        zIndex: 1000,
        backdropFilter: "blur(8px)",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.paper, 0.4)
            : alpha(theme.palette.background.paper, 0.8),
        boxShadow: (theme) => theme.shadows[15],
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.05)}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          height: "100%",
          animation: "scroll 40s linear infinite",
          "@keyframes scroll": {
            "0%": {
              transform: "translateX(0)",
            },
            "100%": {
              transform: "translateX(-33.333%)",
            },
          },
        }}
      >
        {duplicatedRisks.map((risk, index) => (
          <Box
            key={`${risk.id}-${index}`}
            sx={{ px: 3, borderLeft: "1px solid", borderColor: "divider" }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              {getImpactIcon(risk.impact)}
              <Typography
                variant="body2"
                fontWeight="600"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {risk.title}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} gap={0.5}>
              <Chip
                label={risk.id}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: "0.65rem" }}
              />

              <Chip
                label={risk.type}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: "0.65rem" }}
              />
              <Chip
                label={`Impact: ${risk.impact}`}
                size="small"
                color={getImpactColor(risk.impact) as any}
                sx={{ height: 20, fontSize: "0.65rem" }}
              />
              <Chip
                label={`Prob: ${risk.probability}`}
                size="small"
                color={getImpactColor(risk.probability) as any}
                sx={{ height: 20, fontSize: "0.65rem" }}
              />
            </Stack>
          </Box>
        ))}
      </Box>
    </Card>
  );
};
